import React from 'react'
import {
  Box,
  Typography,
  ButtonBase,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Skeleton,
} from '@mui/material'
import FormatAlignLeft from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenter from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRight from '@mui/icons-material/FormatAlignRight'
import FormatBold from '@mui/icons-material/FormatBold'
import FormatUnderlined from '@mui/icons-material/FormatUnderlined'
import FormatItalic from '@mui/icons-material/FormatItalic'
import StrikethroughS from '@mui/icons-material/StrikethroughS'
import KeyboardReturn from '@mui/icons-material/KeyboardReturn'
import PlaylistAdd from '@mui/icons-material/PlaylistAdd'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import Add from '@mui/icons-material/Add'
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline'
import { setLinearLoading } from '../../../component/linear-determinate';
import { apiManage_rbp as apiManage } from '../../../request';
import _ from 'lodash';
import CommonInput from '../../../component/form/common-input.component'
import LoadingOverlayButton from '../../../component/button/loading-overlay-button.component';
import '../scss/print-template.scss'
import { Restaurant } from '../../../models/restaurant.entity';
import { Template, TemplateItem2 } from '../../../models/printer-template.entity';
import GlobalNotification from '../../../component/common/snackbar';
import ContentDetail from './content-detail'
import {
  widthMap,
  TestDataType,
  widthMapType,
  testData,
  PRINT_TEMPLATE_WHITE_LIST,
  PRINT_TEMPLATE_DISABLE_LOCATION_KEYS,
  PRINT_TEMPLATE_DISABLE_SIZE_KEYS,
  PRINT_TEMPLATE_NEWLINE,
  PRINT_TEMPLATE_LOGO_FIELD_KEYS,
  PRINT_TEMPLATE_FONT_OPTIONS,
} from './constants'
import i18n from '../../../i18n';
import { ReactComponent as IcRoundSaveIcon } from '../../../static/images/svg/ic_round-save.svg';
import { MenuListItem } from '../../../component/menu-list/menu-list-component';
import { PrintModuleAuxiliaryModeContext } from '../print-module-auxiliary-mode-context';

/** 与 RBP2 下拉一致：模板里可能出现 LEFT/CENTER/RIGHT，统一为小写 */
function alignSelectValue(align?: string): 'left' | 'center' | 'right' {
  const a = (align ?? 'left').toString().toLowerCase()
  if (a === 'center') return 'center'
  if (a === 'right') return 'right'
  return 'left'
}

/** Figma 2117:91475 — 左/中/右对齐分段控件（图标 + 选中浅底） */
function PrintTemplateAlignSegment(props: {
  value: 'left' | 'center' | 'right'
  disabled?: boolean
  onChange: (v: 'left' | 'center' | 'right') => void
}) {
  const { value, disabled, onChange } = props
  return (
    <ToggleButtonGroup
      exclusive
      fullWidth
      disabled={disabled}
      value={value}
      className="print-template-page__align-segment"
      onChange={(_e, v) => {
        if (v !== null) {
          onChange(v as 'left' | 'center' | 'right')
        }
      }}
    >
      <ToggleButton value="left" disableRipple aria-label={i18n.t('print_template_page_align_left')}>
        <FormatAlignLeft sx={{ fontSize: 20 }} />
      </ToggleButton>
      <ToggleButton value="center" disableRipple aria-label={i18n.t('print_template_page_align_center')}>
        <FormatAlignCenter sx={{ fontSize: 20 }} />
      </ToggleButton>
      <ToggleButton value="right" disableRipple aria-label={i18n.t('print_template_page_align_right')}>
        <FormatAlignRight sx={{ fontSize: 20 }} />
      </ToggleButton>
    </ToggleButtonGroup>
  )
}

/** 粗体/下划线/斜体/删除线；`unified` 时仅内层图标行（由外层统一描边容器包裹） */
function PrintTemplateFontStyleToolbar(props: {
  disabled?: boolean
  field: TemplateItem2
  onUpdate: () => void
  unified?: boolean
}) {
  const { disabled, field, onUpdate, unified } = props
  const toggle = (key: 'bold' | 'underline' | 'italic' | 'strikeout') => {
    if (disabled) {
      return
    }
    if (key === 'bold') {
      field.bold = !field.bold
    } else if (key === 'underline') {
      field.underline = !field.underline
    } else if (key === 'italic') {
      field.italic = !field.italic
    } else {
      field.strikeout = !field.strikeout
    }
    onUpdate()
  }
  const item = (
    key: 'bold' | 'underline' | 'italic' | 'strikeout',
    active: boolean,
    icon: React.ReactNode,
    labelKey: string,
  ) => (
    <IconButton
      key={key}
      size="small"
      disabled={disabled}
      aria-pressed={active}
      aria-label={i18n.t(labelKey)}
      title={i18n.t(labelKey)}
      onClick={() => toggle(key)}
      className={`print-template-page__font-style-btn${active ? ' print-template-page__font-style-btn--selected' : ''}`}
    >
      {icon}
    </IconButton>
  )
  const icons = (
    <Box className="print-template-page__font-toolbar-icons" role="toolbar" aria-label={i18n.t('print_template_page_font')}>
      {item('bold', !!field.bold, <FormatBold sx={{ fontSize: 22 }} />, 'print_template_page_font_bold')}
      {item('underline', !!field.underline, <FormatUnderlined sx={{ fontSize: 22 }} />, 'print_template_page_font_underline')}
      {item('italic', !!field.italic, <FormatItalic sx={{ fontSize: 22 }} />, 'print_template_page_font_italic')}
      {item('strikeout', !!field.strikeout, <StrikethroughS sx={{ fontSize: 22 }} />, 'print_template_page_font_strike')}
    </Box>
  )
  if (unified) {
    return icons
  }
  return (
    <Box
      className="print-template-page__field-control print-template-page__field-control--font-toolbar"
      role="group"
      aria-label={i18n.t('print_template_page_font')}
    >
      {icons}
    </Box>
  )
}

type PrintTemplateFieldOpsBarBtn = {
  key: string
  labelKey: string
  icon: React.ReactNode
  onClick: () => void
  disabled?: boolean
  ariaPressed?: boolean
  selected?: boolean
  /** 禁用时仍显示 Tooltip 时包一层 span（MUI） */
  tooltipWrap?: boolean
}

/** 字体样式 + 分割线 + 换行/字段/空行操作（合并为一行；`fieldOps` 省略时仅字体条，如模板 20） */
function PrintTemplateFontFieldOpsBar(props: {
  disabled?: boolean
  field: TemplateItem2
  onUpdate: () => void
  fieldOps?: {
    hasNewLine: boolean
    onToggleWrap: () => void
    disableDelField: boolean
    onAddField: () => void
    onDelField: () => void
    onAddBlank: () => void
    onDelBlank: () => void
  }
}) {
  const { disabled, field, onUpdate, fieldOps } = props
  const btn = 'print-template-page__font-style-btn'
  const icon = { fontSize: 22 } as const

  if (!fieldOps) {
    return (
      <PrintTemplateFontStyleToolbar disabled={disabled} field={field} onUpdate={onUpdate} />
    )
  }

  const {
    hasNewLine,
    onToggleWrap,
    disableDelField,
    onAddField,
    onDelField,
    onAddBlank,
    onDelBlank,
  } = fieldOps

  const opsBarDividerCls =
    'print-template-page__font-toolbar-divider print-template-page__font-ops-unified__divider'

  const fieldOpsGroups: PrintTemplateFieldOpsBarBtn[][] = [
    [
      {
        key: 'wrap',
        labelKey: 'print_template_page_wrap_info',
        icon: <KeyboardReturn sx={icon} />,
        onClick: onToggleWrap,
        disabled,
        ariaPressed: hasNewLine,
        selected: hasNewLine,
        tooltipWrap: true,
      },
    ],
    [
      {
        key: 'addField',
        labelKey: 'print_template_page_add_field',
        icon: <PlaylistAdd sx={icon} />,
        onClick: onAddField,
      },
      {
        key: 'delField',
        labelKey: 'print_template_page_delete_field',
        icon: <DeleteOutline sx={icon} />,
        onClick: onDelField,
        disabled: disableDelField,
        tooltipWrap: true,
      },
    ],
    [
      {
        key: 'addBlank',
        labelKey: 'print_template_page_add_blank_line',
        icon: <Add sx={icon} />,
        onClick: onAddBlank,
      },
      {
        key: 'delBlank',
        labelKey: 'print_template_page_delete_blank_line',
        icon: <RemoveCircleOutline sx={icon} />,
        onClick: onDelBlank,
      },
    ],
  ]

  return (
    <Box
      className="print-template-page__field-control print-template-page__field-control--font-ops-unified"
      role="group"
    >
      <PrintTemplateFontStyleToolbar unified disabled={disabled} field={field} onUpdate={onUpdate} />
      {fieldOpsGroups.map((group, gi) => (
        <React.Fragment key={`field-ops-${gi}`}>
          <span className={opsBarDividerCls} aria-hidden />
          {group.map((b) => {
            const label = i18n.t(b.labelKey)
            const button = (
              <IconButton
                type="button"
                size="small"
                disabled={b.disabled}
                aria-pressed={b.ariaPressed}
                aria-label={label}
                onClick={b.onClick}
                className={b.selected ? `${btn} print-template-page__font-style-btn--selected` : btn}
              >
                {b.icon}
              </IconButton>
            )
            return (
              <Tooltip key={b.key} title={label}>
                {b.tooltipWrap ? <span>{button}</span> : button}
              </Tooltip>
            )
          })}
        </React.Fragment>
      ))}
    </Box>
  )
}

/** 首屏数据加载中：与三栏布局同结构，避免闪空白 */
function PrintTemplatePageSkeleton() {
  const rowKeys = [0, 1, 2, 3, 4]
  const fieldKeys = [0, 1, 2, 3]
  return (
    <Box className="print-template-page print-template-page--skeleton">
      <Box className="print-template-page__layout print-template">
        <Box className="print-template-page__column print-template-page__column--list">
          <Box className="print-template-page__list-head">
            <Skeleton variant="text" width="75%" height={32} sx={{ mx: 'auto', borderRadius: 1 }} />
          </Box>
          <Box className="print-template-page__template-scroll">
            {rowKeys.map((k) => (
              <Skeleton key={`sk-tpl-${k}`} variant="rounded" height={72} sx={{ borderRadius: '8px', flexShrink: 0 }} />
            ))}
          </Box>
        </Box>
        <Box
          className="print-template-page__column print-template-page__column--preview"
          sx={{ flex: '1 1 0', minWidth: 0 }}
        >
          <Skeleton variant="text" width="55%" height={28} sx={{ mx: 'auto', borderRadius: 1, flexShrink: 0 }} />
          <Skeleton variant="rounded" sx={{ borderRadius: 2, flex: 1, minHeight: 240, width: '100%' }} />
        </Box>
        <Box className="print-template-page__column print-template-page__column--config">
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Skeleton variant="text" width={160} height={28} />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Skeleton variant="rounded" width={100} height={40} />
              <Skeleton variant="rounded" width={100} height={40} />
            </Box>
          </Box>
          {fieldKeys.map((k) => (
            <Box key={`sk-f-${k}`} sx={{ width: '100%' }}>
              <Skeleton variant="text" width="32%" height={22} sx={{ mb: 1, borderRadius: 1 }} />
              <Skeleton variant="rounded" height={48} sx={{ borderRadius: 1 }} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default class PrintTemplate extends React.Component<Prop, State> {
  static contextType = PrintModuleAuxiliaryModeContext
  context!: React.ContextType<typeof PrintModuleAuxiliaryModeContext>

  public contentRef: React.RefObject<any> | null = null
  public calcWidthRef: React.RefObject<any> | null = null

  /** 门店所在国家名（与 RBP2 `country` 一致） */
  public country = ''
  /** 门店名称（与 RBP2 一致） */
  public restaurantName = ''
  /** 预结打印机关联打印头文案 */
  public headInfo = ''
  /** KDS 税务主体信息（与 RBP2 `taxInfo` 一致） */
  public taxInfo: any = {}
  /** 模板 id → 默认内容 JSON（与 RBP2 `originalContentMap` 一致） */
  public originalContentMap: Record<number, string> = {}

  public state: State = {
    menuList: [],
    activeMenu: '',
    content: [],
    field: null,
    fieldOptions: [],
    hasNewLine: false,
    fontOptions: [],
    recoverSubmitting: false,
    saveSubmitting: false,
    initialLoading: true,
  }

  // *********************
  // Life Cycle Function
  // *********************
  async componentDidMount() {
    this.contentRef = React.createRef()
    this.calcWidthRef = React.createRef()
    const countryRes = await apiManage.get_restaurant_info() as Restaurant & { error?: boolean }
    if (!(countryRes as { error?: boolean }).error) {
      this.country = String(_.get(countryRes, 'country', '') || _.get(countryRes, 'name', '')).trim()
      this.restaurantName = String(_.get(countryRes, 'reg_name', '') || _.get(countryRes, 'nickname', '')).trim()
    }
    await this.init()
  }

  // *********************
  // Default Function
  // *********************
  getOriginalContent = async (): Promise<void> => {
    const results = await Promise.all(
      PRINT_TEMPLATE_WHITE_LIST.map((id) => apiManage.getDefaultPrintTemplate(id))
    )
    const map: Record<number, string> = {}
    results.forEach((item: Template & { error?: boolean }) => {
      if (!item?.error && item?.template_id != null) {
        const contentMap = {
          Spain: item.spain_content,
          France: item.fr_content,
        } as Record<string, string | undefined>
        map[item.template_id] = String(
          (this.country ? contentMap[this.country] : undefined) ?? item.original_content ?? ''
        )
      }
    })
    this.originalContentMap = map;
    console.log('this.originalContentMap', this.originalContentMap)
  }

  pickTemplateContentString(row: Template): string {
    if (this.country === 'Spain' && row.spain_content) {
      return String(row.spain_content)
    }
    if (this.country === 'France' && row.fr_content) {
      return String(row.fr_content)
    }
    return typeof row.content === 'string' ? row.content : JSON.stringify(row.content ?? [])
  }

  getTaxInfo = async (): Promise<void> => {
    const isSpain = this.country === 'Spain'
    const restRes = await apiManage.postKdsRestaurantInfoList(isSpain)
    if (restRes?.error || !_.isArray(restRes)) {
      return
    }
    const res = restRes[0] || {}
    this.taxInfo = {
      denominazione: _.get(res, 'Denominazione', ''),
      indirizzo: _.get(res, 'Indirizzo', ''),
      city: _.get(res, 'Comune', ''),
      province: _.get(res, 'Provincia', ''),
      cap: _.get(res, 'CAP', ''),
      codiceFiscale: _.get(res, isSpain ? 'nif' : 'CodiceFiscale', ''),
      IdCodice: _.get(res, isSpain ? 'cif' : 'IdCodice', ''),
      territory: _.get(res, 'territory', ''),
    }
  }

  getTestPrinterHeader = async (): Promise<void> => {
    try {
      const [res2, res3] = await Promise.all([
        apiManage.getPrinterDevice({}),
        apiManage.getPrinterHead(),
      ])
      const printers = _.get(res2, 'error') ? [] : (res2 as unknown[])
      const headers = _.get(res3, 'error') ? [] : (res3 as unknown[])
      const testPrint = (printers as { guest_check_printer?: number; header?: number }[]).find(
        (p) => p?.guest_check_printer && p.guest_check_printer > 0
      )
      if (!testPrint) {
        return
      }
      const header = (headers as { descriptors_headers_id?: number }[]).find(
        (h) => h?.descriptors_headers_id === testPrint.header
      ) as Record<string, string> | undefined
      if (!header) {
        return
      }
      let str = ''
      for (let i = 1; i <= 6; i++) {
        const line = _.get(header, `line_${i}`, '')
        if (line) {
          str += `${line}\n`
        }
      }
      this.headInfo = str
    } catch {
      this.headInfo = ''
    }
  }

  async init() {
    setLinearLoading(true);
    try {
      await this.getOriginalContent();
      const res = await apiManage.getPrintTemplate();
      const rawList: Template[] = _.get(res, 'template', []) || [];
      const template = rawList.filter((item) =>
        (PRINT_TEMPLATE_WHITE_LIST as readonly number[]).includes(item.template_id)
      )
      if (!_.get(res, 'error') && template.length > 0) {
        template.forEach((row) => {
          row.content = this.pickTemplateContentString(row) as Template['content']
        })
        const contentStr = typeof template[0].content === 'string' ? template[0].content : '[]';
        const content = _.cloneDeep(JSON.parse(contentStr)) as TemplateItem2[];
        this.setState(
          {
            menuList: template.map((item) => ({
              title: item.template_name,
              key: `${item.template_id}`,
              dataSource: item,
            })),
            activeMenu: `${template[0].template_id}`,
            content,
            field: content[0],
            hasNewLine: this.checkHasNewLine(content, content[0]),
            fontOptions: PRINT_TEMPLATE_FONT_OPTIONS,
          },
          () => {
            this.setState({
              fieldOptions: this.getFieldOptions(content, content[0]),
            });
          }
        )
        await this.getTestPrinterHeader();
        await this.getTaxInfo();
      }
    } finally {
      setLinearLoading(false);
      this.setState({ initialLoading: false });
    }
  }

  // 获取字段来源（与 RBP2：基于默认模板 `originalContentMap` + `field.key` 定位同行）
  getFieldOptions(_content: TemplateItem2[], field: TemplateItem2) {
    const contentStr = this.originalContentMap?.[Number(this.state.activeMenu)]
    const originContent: TemplateItem2[] = contentStr ? _.cloneDeep(JSON.parse(contentStr)) : []
    let fieldOptions: Array<{ label: string; value: string }> = []
    const set = new Set<string>()
    const cb = (item1: TemplateItem2, _index: number, data: TemplateItem2[]) => {
      if (item1.key === field.key) {
        fieldOptions = data
          .filter((item) => {
            if (item?.key && !Array.isArray(item.value)) {
              if (set.has(String(item.key))) {
                return false
              }
              set.add(String(item.key))
              return true
            }
            return false
          })
          .map((item) => ({ value: String(item.key), label: String(item.key) }))
        return true
      }
      return false
    }
    originContent.find((item, index) => {
      return typeof item.value === 'string'
        ? cb(item, index, originContent)
        : Boolean(
          (item.value as TemplateItem2[]).find((item2, index2) => {
            return cb(item2, index2, item.value as TemplateItem2[])
          })
        )
    })
    if (this.country === 'Italy' && this.state.activeMenu === '17') {
      fieldOptions = fieldOptions.filter((temp) => temp?.value !== 'total_without_tax')
    }
    return fieldOptions
  }

  /**
   * 当前选中模板在测试数据中的纸宽（毫米），用于预览区标题；无匹配时默认 80
   */
  getPaperWidthMm(): number {
    const id = this.state.activeMenu as TestDataType
    const row = testData[id] as { paper_width?: number } | undefined
    const w = row?.paper_width
    return typeof w === 'number' && w > 0 ? w : 80
  }

  /**
   * 从接口行数据解析模板列表副标题（设计稿中文案位），无则返回空串
   */
  getTemplateListSubtitle(dataSource: Record<string, unknown>): string {
    const v = dataSource.template_description ?? dataSource.remark ?? dataSource.description
    return typeof v === 'string' ? v : ''
  }

  // 点击模板字段（与 RBP2：用 _.isEqual 解析为 state.content 树中的同一引用）
  public onClickItem = (item: TemplateItem2) => {
    let current: TemplateItem2 | null = null
    const cb = (item1: TemplateItem2) => {
      if (_.isEqual(item1, item)) {
        current = item1
        return true
      }
      return false
    }
    this.state.content.find((temp) => {
      return typeof temp.value === 'string'
        ? cb(temp)
        : Boolean((temp.value as TemplateItem2[]).find((t2) => cb(t2)))
    })
    this.setState({
      field: current,
      fieldOptions: this.getFieldOptions(this.state.content, item),
      hasNewLine: this.checkHasNewLine(this.state.content, item),
    })
  }

  /** 下一行是否为换行占位（与 RBP2 `checkHasNewLine` 一致） */
  public checkHasNewLine = (content: TemplateItem2[], field: TemplateItem2) => {
    let hasNewLine = false
    const isNl = (v: unknown) => v === PRINT_TEMPLATE_NEWLINE || v === '\n'
    const cb = (item1: TemplateItem2, index: number, data: TemplateItem2[]) => {
      if (_.isEqual(item1, field) && data[index + 1] && isNl(data[index + 1].value)) {
        hasNewLine = true
        return true
      }
      return false
    }
    content.find((item, index) => {
      return typeof item.value === 'string'
        ? cb(item, index, content)
        : Boolean(
          (item.value as TemplateItem2[]).find((item2, index2) => {
            return cb(item2, index2, item.value as TemplateItem2[])
          })
        )
    })
    return hasNewLine
  }

  /** 切换「换行」占位（与 RBP2 `handleChangeNewLine` 一致） */
  handleChangeNewLine(checked: boolean) {
    const { content, field } = this.state
    const cb = (item1: TemplateItem2, index: number, data: TemplateItem2[]) => {
      if (_.isEqual(item1, field)) {
        if (checked) {
          data.splice(index + 1, 0, { value: PRINT_TEMPLATE_NEWLINE })
        } else if (index + 1 < data.length) {
          data.splice(index + 1, 1)
        }
        return true
      }
      return false
    }
    content.find((item, index) => {
      return typeof item.value === 'string'
        ? cb(item, index, content)
        : Boolean(
          (item.value as TemplateItem2[]).find((item2, index2) => {
            return cb(item2, index2, item.value as TemplateItem2[])
          })
        )
    })
    this.setState({ content, hasNewLine: checked })
  }

  // 预览
  public onPreview = async () => {
    this.contentRef?.current?.showPreview()
  }

  // 恢复默认设置（与 RBP2：拉取 `getDefaultPrintTemplate` 后按国家选内容，不写 patch 为旧 original）
  public onRecover = async () => {
    const menu = this.state.menuList.find((item) => item.key === this.state.activeMenu)
    if (!menu) {
      return
    }
    const ds = menu.dataSource as Template
    let contentStr = String(ds.original_content ?? '')
    const res = await apiManage.getDefaultPrintTemplate(Number(menu.key))
    if (!_.get(res, 'error')) {
      const contentMap = {
        Spain: res.spain_content,
        France: res.fr_content,
      } as Record<string, string | undefined>
      contentStr = String((this.country ? contentMap[this.country] : undefined) ?? res.original_content ?? '')
    }
    const content = _.cloneDeep(JSON.parse(contentStr)) as TemplateItem2[]
    ds.content = contentStr
    this.setState({ activeMenu: '' }, () => {
      this.setState(
        {
          activeMenu: menu.key,
          menuList: this.state.menuList.map((m) => m),
          content,
          field: content[0],
          hasNewLine: this.checkHasNewLine(content, content[0]),
        },
        () => {
          this.setState({ fieldOptions: this.getFieldOptions(content, content[0]) })
        }
      )
      this.contentRef?.current?.resetActiveKey?.()
    })
  }

  /** 实际保存（与 RBP2：`canSave` + `sort` + patch） */
  persistPrintTemplate = async (): Promise<void> => {
    if (this.contentRef?.current?.canSave && !this.contentRef.current.canSave(this.state.content)) {
      return
    }
    const sortContent = this.state.content.map((item, index) => {
      _.set(item, 'sort', index + 1)
      return item
    })
    const content2String = JSON.stringify(sortContent)
    const menu = this.state.menuList.find((item) => item.key === this.state.activeMenu)
    if (menu) {
      const res = await apiManage.savePrintTemplate(Number(this.state.activeMenu), {
        template_id: Number(this.state.activeMenu),
        template_name: menu.title,
        template_content: content2String,
      })
      if (!_.get(res, 'error')) {
        GlobalNotification.success(i18n.t('google_text_public_text_save_successfully'))
          ; (menu.dataSource as Template).content = content2String
        this.setState({
          menuList: this.state.menuList.map((m) => m),
        })
      } else {
        GlobalNotification.error(i18n.t('global_save_text_failed'))
      }
    }
  }

  /** 保存：带按钮 loading，避免重复提交（原 RBP2 防抖由 loading 态替代） */
  handleSaveClick = async (): Promise<void> => {
    if (this.state.saveSubmitting) {
      return
    }
    this.setState({ saveSubmitting: true })
    try {
      await this.persistPrintTemplate()
    } finally {
      this.setState({ saveSubmitting: false })
    }
  }

  handleRecoverClick = async (): Promise<void> => {
    if (this.state.recoverSubmitting) {
      return
    }
    this.setState({ recoverSubmitting: true })
    try {
      await this.onRecover()
    } finally {
      this.setState({ recoverSubmitting: false })
    }
  }

  // 新增字段（与 RBP2：在下一字段前插入 NEWLINE + 占位 + NEWLINE）
  public handleAddField = () => {
    const { content, field } = this.state
    const cb = (item: TemplateItem2, index: number, data: TemplateItem2[]) => {
      if (_.isEqual(field, item)) {
        const _index = index
        const curData = data.slice(_index + 1)
        const idx = curData.findIndex((i) => i.key)
        data.splice(_index + idx + 1, 0, { value: PRINT_TEMPLATE_NEWLINE })
        data.splice(_index + idx + 2, 0, { value: i18n.t('print_template_page_please_edit') })
        data.splice(_index + idx + 3, 0, { value: PRINT_TEMPLATE_NEWLINE })
        return true
      }
      return false
    }
    content.find((item, index) => {
      return typeof item.value === 'string'
        ? cb(item, index, content)
        : Boolean(
          (item.value as TemplateItem2[]).find((item2, index2) => {
            return cb(item2, index2, item.value as TemplateItem2[])
          })
        )
    })
    this.setState({ content })
  }

  // 新增空行
  public handleAddEmptyLine = () => {
    const { content, field } = this.state
    const isBreak = (v: unknown) =>
      typeof v === 'string' && (v === PRINT_TEMPLATE_NEWLINE || v === '\n' || /\n/.test(v))
    const cb = (item: TemplateItem2, index: number, data: TemplateItem2[]) => {
      if (_.isEqual(field, item)) {
        let _index = index
        let cur = data[_index]
        while (cur && !isBreak(cur.value)) {
          cur = data[++_index]
        }
        data.splice(_index + 1, 0, { value: PRINT_TEMPLATE_NEWLINE })
        return true
      }
      return false
    }

    content.find((item, index) => {
      return typeof item.value === 'string'
        ? cb(item, index, content)
        : Boolean(
          (item.value as TemplateItem2[]).find((item2, index2) => {
            return cb(item2, index2, item.value as TemplateItem2[])
          })
        )
    })
    this.setState({ content })
  }

  // 删除字段（与 RBP2：分组行内仅一项时去换行 + 扁平上一索引）
  public handleDelField = () => {
    const { content, field } = this.state
    const data = this.contentRef?.current.parseContent(content)
    let prevKey = ''
    let prevField: TemplateItem2 | null = null
    data.find((subData: any, index: number) => {
      return subData.find((item: any, index1: number) => {
        if (_.isEqual(field, item)) {
          if (subData?.length === 1) this.handleChangeNewLine(false);
          console.log('删除的index', index, 'index1', index1)
          if (index1 > 0) {
            prevKey = `${index}-${index1 - 1}`
            prevField = data[index][index1 - 1]
          } else {
            if (index !== 0) {
              prevKey = `${index - 1}-${data[index - 1].length - 1}`
              prevField = data[index - 1][data[index - 1].length - 1]
            }
            // 如果是第一个字段, 则指向下一个字段
          }
          return true
        }
        return false
      })
    })

    const cb = (item: TemplateItem2, index: number, data: TemplateItem2[]) => {
      if (_.isEqual(field, item)) {
        data.splice(index, 1)
        return true
      }
      return false
    }

    content.find((item, index) => {
      return typeof item.value === 'string'
        ? cb(item, index, content)
        : item.value.find((item2, index2) => {
          return cb(item2, index2, item.value as TemplateItem2[])
        })
    })

    let options = this.state.fieldOptions
    let hasNewLine = this.state.hasNewLine
    if (prevField) {
      options = this.getFieldOptions(content, prevField)
      hasNewLine = this.checkHasNewLine(content, prevField)
    }

    // 寻找相同的字段
    let current: TemplateItem2 | null = null;
    const cb2 = (item1: TemplateItem2) => {
      if (_.isEqual(item1, prevField)) {
        current = item1;
        return true
      }
      return false
    }
    content.find((temp) => {
      return typeof temp.value === 'string'
        ? cb2(temp)
        : temp.value.find((temp2) => {
          return cb2(temp2)
        })
    })

    this.setState({
      content: content,
      field: current,
      fieldOptions: options,
      hasNewLine
    }, () => {
      // this.handleDelEmptyLine(false)
    })
    this.contentRef?.current.resetActiveKey(prevKey)

  }

  // 删除空行
  public handleDelEmptyLine = (tip: boolean = true) => {
    const { content, field } = this.state
    const cb = (item: TemplateItem2, index: number, data: TemplateItem2[]) => {
      if (_.isEqual(field, item)) {
        if (index + 1 >= data.length) return false
        let _index = index
        let cur = data[_index]
        // 循环找到第一个有换行符的字段
        while (cur && !/NEWLINE/.test(cur.value as string)) {
          cur = data[++_index]
        }
        if (_index + 1 >= data.length) return false
        // 看看他的后面是不是个换行符
        data[_index + 1].value === 'NEWLINE'
          ? data.splice(_index + 1, 1)
          : tip && GlobalNotification.error(i18n.t('print_template_page_next_line_not_empty'))
        return true
      }
      return false
    }
    content.find((item, index) => {
      return typeof item.value === 'string'
        ? cb(item, index, content)
        : item.value.find((item2, index2) => {
          return cb(item2, index2, item.value as TemplateItem2[])
        })
    })
    this.setState({ content: content })
  }

  // *********************
  // View
  // *********************
  render() {
    if (this.state.initialLoading) {
      return <PrintTemplatePageSkeleton />
    }
    const { menuList, activeMenu, content, field, fontOptions, hasNewLine } = this.state;
    const activeTemplate =
      (menuList.find((m) => m.key === activeMenu)?.dataSource as Template | undefined) ?? null;
    const paperMm = this.getPaperWidthMm();
    let fieldOptions = this.state.fieldOptions;
    let disableLocation = false;
    let disableSize = false;
    const logoKeys = PRINT_TEMPLATE_LOGO_FIELD_KEYS as unknown as string[];
    if (this.country === 'Spain' && ['24', '26'].includes(activeMenu)) {
      disableLocation = Boolean(
        field?.key &&
        typeof field.key === 'string' &&
        PRINT_TEMPLATE_DISABLE_LOCATION_KEYS.includes(field.key)
      )
      disableSize = Boolean(
        field?.key &&
        typeof field.key === 'string' &&
        PRINT_TEMPLATE_DISABLE_SIZE_KEYS.includes(field.key)
      )
      fieldOptions = fieldOptions.filter(
        (temp) => !PRINT_TEMPLATE_DISABLE_LOCATION_KEYS.includes(temp.value)
      )
    }
    const listHeading = this.country
      ? i18n.t('print_template_page_list_heading', {
        region: this.country.replace(/\s+/g, '').toUpperCase(),
      })
      : i18n.t('print_template_page_label');
    return (
      <Box className="print-template-page">
        <Box className="print-template-page__layout print-template">
          <Box className="print-template-page__column print-template-page__column--list">
            <Box className="print-template-page__list-head">
              <Typography component="h2" className="print-template-page__list-title" title={listHeading}>
                {listHeading}
              </Typography>
            </Box>
            <Box className="print-template-page__template-scroll">
              {menuList.map((menu) => {
                const isActive = activeMenu === menu.key
                const subtitle = this.getTemplateListSubtitle(menu.dataSource as Record<string, unknown>)
                return (
                  <ButtonBase
                    key={menu.key}
                    className={`print-template-page__template-item${isActive ? ' print-template-page__template-item--active' : ''}`}
                    focusRipple
                    onClick={async () => {
                      const nextRaw = this.pickTemplateContentString(menu.dataSource as Template);
                      const nextContent = _.cloneDeep(JSON.parse(nextRaw)) as TemplateItem2[];

                      this.setState(
                        {
                          activeMenu: menu.key,
                          content: nextContent,
                          field: nextContent[0],
                          hasNewLine: this.checkHasNewLine(nextContent, nextContent[0]),
                        },
                        () => {
                          this.setState({
                            fieldOptions: this.getFieldOptions(nextContent, nextContent[0]),
                          })
                        }
                      )
                      this.contentRef?.current?.resetActiveKey?.()
                    }}
                  >
                    <Typography component="p" className="print-template-page__template-item-title">
                      {menu.title}
                    </Typography>
                    {subtitle && this.context?.auxiliaryModeEnabled ? (
                      <Typography component="p" className="print-template-page__template-item-desc">
                        {subtitle}
                      </Typography>
                    ) : null}
                  </ButtonBase>
                )
              })}
            </Box>
          </Box>

          <ContentDetail
            key={String(activeMenu)}
            content={content}
            onClickItem={this.onClickItem}
            ref={this.contentRef}
            calcWidthRef={this.calcWidthRef?.current ?? null}
            templateId={activeMenu as TestDataType}
            width={widthMap[activeMenu as widthMapType]}
            template={activeTemplate}
            headInfo={this.headInfo}
            taxInfo={this.taxInfo}
            country={this.country}
            LogoKey={logoKeys}
            paperMm={paperMm}
          />

          {field ? (
            <Box className="print-template-page__column print-template-page__column--config">
              <Box className="print-template-page__config-toolbar">
                <Typography component="h2" className="print-template-page__config-toolbar-title">
                  {i18n.t('print_template_page_style_config_title')}
                </Typography>
                <Box className="print-template-page__config-toolbar-actions">
                  <LoadingOverlayButton
                    type="button"
                    variant="contained"
                    color="inherit"
                    disableElevation
                    className="print-template-page__btn-recover"
                    loading={this.state.recoverSubmitting}
                    loadingIndicatorSize={22}
                    sx={{
                      backgroundColor: 'var(--fill-primary-200)',
                      color: 'var(--text-dark)',
                      fontWeight: 500,
                      '&:hover': { backgroundColor: 'var(--stroke-primary-100)' },
                    }}
                    onClick={() => { void this.handleRecoverClick(); }}
                  >
                    {i18n.t('print_template_page_recover_default_short')}
                  </LoadingOverlayButton>
                  <LoadingOverlayButton
                    type="button"
                    variant="contained"
                    color="inherit"
                    disableElevation
                    className="print-template-page__btn-save"
                    loading={this.state.saveSubmitting}
                    loadingIndicatorSize={22}
                    startIcon={<IcRoundSaveIcon style={{ width: 24, height: 24 }} aria-hidden />}
                    sx={{
                      backgroundColor: 'var(--fill-primary-300)',
                      color: 'var(--text-light)',
                      fontWeight: 700,
                      '&:hover': { backgroundColor: 'var(--icon-primary-300)' },
                    }}
                    onClick={() => { void this.handleSaveClick(); }}
                  >
                    {i18n.t('print_template_page_save_changes')}
                  </LoadingOverlayButton>
                </Box>
              </Box>

              <Box className="print-template-page__config-form">
                {activeMenu !== '20' ? (
                  <>
                    {!(typeof field.key === 'string' && logoKeys.includes(field.key)) ? (
                      <Box className="print-template-page__config-field">
                        <Typography component="label" className="print-template-page__field-label">
                          {i18n.t('print_template_page_field_format')}
                        </Typography>
                        <Box className="print-template-page__field-control">
                          <CommonInput
                            type="text"
                            variant="outlined"
                            fullWidth
                            disabled={disableLocation}
                            value={(field.value as string).replace(/\n/g, '\\n')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              field.value = event.target.value.replace(/\\n/g, '\n')
                              const match = event.target.value.match(/%(.*)%/)
                              field.type = match ? 'FORMAT' : 'CONSTANT'
                              this.setState({ field })
                            }}
                          />
                        </Box>
                      </Box>
                    ) : null}
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {i18n.t('print_template_page_field_source')}
                      </Typography>
                      <Box className="print-template-page__field-control">
                        {disableLocation ? (
                          <CommonInput
                            type="text"
                            variant="outlined"
                            fullWidth
                            disabled
                            value={field.key as string}
                          />
                        ) : (
                          <CommonInput
                            inputType="SELECT"
                            fullWidth
                            disabled={disableLocation}
                            value={(field.key as string) || ''}
                            items={fieldOptions}
                            onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                              const val = String(e.target.value)
                              field.key = val
                              const value = field.value as string
                              const match = value.match(/%(.*)%/)
                              if (match && match[1]) {
                                field.value = value.replace(/%(.*)%/, () => `%${val}%`)
                              } else if (value.includes('\n')) {
                                const index = value.indexOf('\n')
                                field.value = value.slice(0, index) + `%${val}%` + value.slice(index)
                              } else {
                                field.value = value + `%${val}%`
                              }
                              if (!field.type) {
                                field.type = 'FORMAT'
                              }
                              this.setState({ field })
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    {typeof field.key === 'string' && logoKeys.includes(field.key) ? (
                      <>
                        <Box className="print-template-page__config-field">
                          <Typography component="label" className="print-template-page__field-label">
                            {i18n.t('print_template_page_photo_width')}
                          </Typography>
                          <Box className="print-template-page__field-control">
                            <CommonInput
                              type="number"
                              variant="outlined"
                              fullWidth
                              disabled={disableSize}
                              value={field.logoWidth ?? 80}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const value = Number(event.target.value)
                                if (value >= 0) {
                                  field.logoWidth = value
                                  this.setState({ field })
                                }
                              }}
                            />
                          </Box>
                        </Box>
                        <Box className="print-template-page__config-field">
                          <Typography component="label" className="print-template-page__field-label">
                            {i18n.t('print_template_page_photo_height')}
                          </Typography>
                          <Box className="print-template-page__field-control">
                            <CommonInput
                              type="number"
                              variant="outlined"
                              fullWidth
                              disabled={disableSize}
                              value={field.logoHeight ?? 80}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const value = Number(event.target.value)
                                if (value >= 0) {
                                  field.logoHeight = value
                                  this.setState({ field })
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      </>
                    ) : (
                      <Box className="print-template-page__config-field">
                        <Typography component="label" className="print-template-page__field-label">
                          {i18n.t('print_template_page_width_info')}
                        </Typography>
                        <Box className="print-template-page__field-control">
                          <CommonInput
                            type="number"
                            variant="outlined"
                            fullWidth
                            disabled={disableLocation}
                            value={field.width ?? 0}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              const value = Number(event.target.value)
                              if (value >= 0) {
                                field.width = value
                                this.setState({ field })
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {i18n.t('print_template_page_margin_left')}
                      </Typography>
                      <Box className="print-template-page__field-control">
                        <CommonInput
                          type="number"
                          variant="outlined"
                          fullWidth
                          disabled={disableLocation}
                          value={field.left ?? 0}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const value = Number(event.target.value)
                            if (value >= 0 && value <= 72) {
                              field.left = value
                              this.setState({ field })
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {i18n.t('print_template_page_align')}
                      </Typography>
                      <Box className="print-template-page__field-control">
                        <PrintTemplateAlignSegment
                          disabled={disableLocation}
                          value={alignSelectValue(field.align as string | undefined)}
                          onChange={(v) => {
                            field.align = v
                            this.setState({ field })
                          }}
                        />
                      </Box>
                    </Box>
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {`${i18n.t('print_template_page_font')}${i18n.t('print_template_page_align')}`}
                      </Typography>
                      <Box className="print-template-page__field-control">
                        <PrintTemplateAlignSegment
                          disabled={disableLocation}
                          value={alignSelectValue(field.textAlign)}
                          onChange={(v) => {
                            field.textAlign = v
                            this.setState({ field })
                          }}
                        />
                      </Box>
                    </Box>
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {i18n.t('print_template_page_fonts_list')}
                      </Typography>
                      <Box className="print-template-page__field-control">
                        <CommonInput
                          inputType="SELECT"
                          fullWidth
                          disabled={disableLocation}
                          value={field.font || 'unset'}
                          items={[
                            { label: i18n.t('menu_and_dishes_page_not_specified'), value: 'unset' },
                            ...fontOptions,
                          ]}
                          onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                            const val = String(e.target.value)
                            field.font = val === 'unset' ? undefined : val
                            this.setState({ field })
                          }}
                        />
                      </Box>
                    </Box>
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {i18n.t('print_template_page_font_size')}
                      </Typography>
                      <Box className="print-template-page__field-control">
                        <CommonInput
                          type="number"
                          variant="outlined"
                          fullWidth
                          disabled={disableLocation}
                          value={field.size ?? 0}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const value = Number(event.target.value)
                            field.size = value
                            this.setState({ field })
                          }}
                        />
                      </Box>
                    </Box>
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {`${i18n.t('print_template_page_font')}·${i18n.t('general_text_operating')}`}
                      </Typography>
                      <PrintTemplateFontFieldOpsBar
                        disabled={disableLocation}
                        field={field}
                        onUpdate={() => this.setState({ field })}
                        fieldOps={{
                          hasNewLine,
                          onToggleWrap: () => this.handleChangeNewLine(!hasNewLine),
                          disableDelField: disableLocation,
                          onAddField: () => this.handleAddField(),
                          onDelField: () => this.handleDelField(),
                          onAddBlank: () => this.handleAddEmptyLine(),
                          onDelBlank: () => this.handleDelEmptyLine(),
                        }}
                      />
                    </Box>
                  </>
                ) : field.key === 'head' ? (
                  <>
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {i18n.t('print_template_page_field_source')}
                      </Typography>
                      <Box className="print-template-page__field-control">
                        <CommonInput
                          inputType="SELECT"
                          fullWidth
                          disabled
                          value={(field.key as string) || ''}
                          items={fieldOptions}
                        />
                      </Box>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {i18n.t('print_template_page_field_format')}
                      </Typography>
                      <Box className="print-template-page__field-control">
                        <CommonInput
                          type="text"
                          variant="outlined"
                          fullWidth
                          value={(field.value as string).replace(/\n/g, '\\n')}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            field.value = event.target.value.replace(/\\n/g, '\n')
                            const match = event.target.value.match(/%(.*)%/)
                            field.type = match ? 'FORMAT' : 'CONSTANT'
                            this.setState({ field })
                          }}
                        />
                      </Box>
                    </Box>
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {i18n.t('print_template_page_field_source')}
                      </Typography>
                      <Box className="print-template-page__field-control">
                        <CommonInput
                          inputType="SELECT"
                          fullWidth
                          disabled
                          value={(field.key as string) || ''}
                          items={fieldOptions}
                        />
                      </Box>
                    </Box>
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {i18n.t('print_template_page_fonts_list')}
                      </Typography>
                      <Box className="print-template-page__field-control">
                        <CommonInput
                          inputType="SELECT"
                          fullWidth
                          value={field.font || 'unset'}
                          items={[
                            { label: i18n.t('menu_and_dishes_page_not_specified'), value: 'unset' },
                            ...fontOptions,
                          ]}
                          onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                            const val = String(e.target.value)
                            field.font = val === 'unset' ? undefined : val
                            this.setState({ field })
                          }}
                        />
                      </Box>
                    </Box>
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {i18n.t('print_template_page_font_size')}
                      </Typography>
                      <Box className="print-template-page__field-control">
                        <CommonInput
                          type="number"
                          variant="outlined"
                          fullWidth
                          value={field.size ?? 0}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const value = Number(event.target.value)
                            field.size = value
                            this.setState({ field })
                          }}
                        />
                      </Box>
                    </Box>
                    <Box className="print-template-page__config-field">
                      <Typography component="label" className="print-template-page__field-label">
                        {i18n.t('print_template_page_font')}
                      </Typography>
                      <PrintTemplateFontFieldOpsBar
                        field={field}
                        onUpdate={() => this.setState({ field })}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          ) : null}
        </Box>

        {/* 提供一个div给模板, 模板通过getBoundingClientRect 获取实际内容的宽度 */}
        <div ref={this.calcWidthRef} style={{ position: 'absolute', left: -999999 }}></div>
      </Box>
    )
  }
}

// *********************
// Type
// *********************
type Prop = {}

type State = {
  /** 左侧模板列表 */
  menuList: MenuListItem[];
  /** 当前选中的模板 key */
  activeMenu: string;
  /** 当前模板 JSON 解析后的可编辑树 */
  content: TemplateItem2[];
  /** 右侧正在编辑的字段节点 */
  field: TemplateItem2 | null;
  /** 字段来源下拉可选项 */
  fieldOptions: Array<{ label: string; value: string }>;
  /** 当前字段下一行是否为换行占位（与 RBP2 一致） */
  hasNewLine: boolean;
  /** 接口下发的字体列表（与 RBP2 `fontOptions` 一致） */
  fontOptions: Array<{ label: string; value: string }>;
  recoverSubmitting: boolean;
  saveSubmitting: boolean;
  /** 首屏 init 完成前展示骨架屏 */
  initialLoading: boolean;
}
